<template>
<div>
    <NavigationBar />
    <div class="container" style="padding-top: 20px;">
        <div class="alert alert-danger alert-dismissible d-flex align-items-center fade show" v-if="error">
            <i class="bi-exclamation-octagon-fill"></i>
            <strong class="mx-2">Error!</strong> {{ error }}
            <br />
        </div>
        <h2>Returned Payments Report</h2>
        <div class="searchInputs">
            <div class="searchInputRow">
                <div class="searchInputItem">
                    <label for="startDate" class="col-form-label"><b>Start Date</b></label>
                    <input type="date" class="form-control" id="startDate" v-model="startDate" required>
                </div>
                <div class="searchInputItem">
                    <label for="endDate" class="col-form-label"><b>End Date</b></label>
                    <input type="date" class="form-control" id="endDate" v-model="endDate" required>
                </div>
            </div>
            <div class="searchInputRow">
                <div class="searchInputItem" v-if="regions">
                    <label for="region" class="col-form-label"><b>Region</b></label>
                    <select class="form-select" id="region" v-model="region">
                        <option value="-1" selected>Please select</option>
                        <option v-for="region in regions" :value="region.id" :key="region.id">{{region.name}}</option>
                    </select>
                </div>
                <div class="searchInputItem">
                    <label for="jamatkhana" class="col-form-label"><b>Jamatkhana</b> </label>
                    <select class="form-select" id="jamatkhana" v-model="jamatkhana" :disabled="region === '-1'">
                        <option v-if="regions" value="-1" selected>All Jamatkhanas</option>
                        <option v-for="jk in filterJamatkhanas(region)" :value="jk.id" :key="jk.id">{{jk.name}}</option>
                    </select>
                </div>
            </div>
            <div class="searchInputRow">
                <div class="searchInputItem">
                    <button class="col-auto btn btn-success" :disabled="disableRunReport" @click="runReport">Run Report</button>
                </div>
            </div>
        </div>
        <div v-if="loadingReport">
            <LoadingSpinner />
        </div>
        <div v-if="report.length > 0 && !loadingReport" class="tableResults">
            <table class="table">
                <tbody>
                    <tr>
                        <td><b>Payment ID</b></td>
                        <td><b>Amount</b></td>
                        <td><b>Initiated On</b></td>
                        <td><b>Initiated By</b></td>
                        <td><b>Member ID</b></td>
                        <td><b>Coverage Location</b></td>
                        <td><b>Payment Type</b></td>
                    </tr>
                    <tr v-for="payment in report" :key="payment.id">
                        <td><div class="link" @click="clickViewDetails(payment.id)">{{payment.id}}</div></td>
                        <td>{{displayInUSD(payment.amount)}}</td>
                        <td>{{getLocalTimestampfromUtcTimeStamp(payment.paymentInitiatedAt)}}</td>
                        <td>{{payment.initiatedBy.name}}</td>
                        <td>{{payment.initiatedBy.id}}</td>
                        <td>{{getJamatkhanaName(payment.locationId)}}</td>
                        <td>{{payment.paymentMethod.name}}</td>
                    </tr>
                    <tr>
                        <td><b>Total:</b></td>
                        <td><b>{{displayInUSD(totalAmount)}}</b></td>
                        <td></td>
                        <td><b>Count:</b></td>
                        <td>{{report.length}}</td>
                        <td></td>
                        <td></td>
                    </tr>
                </tbody>
            </table>
        </div>    
    </div>
</div>

</template>

<script>
import axios from 'axios';
import NavigationBar from '../components/NavigationBar.vue';
import LoadingSpinner from '../components/LoadingSpinner.vue';
import { displayInUSD, getLocalTimestampfromUtcTimeStamp, getVolunteerJks } from '../utils/MethodUtils.js';
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

export default {
    name: "ReturnedPaymentsReportPage",
    components: {
        NavigationBar,
        LoadingSpinner
    },
    setup() {
        const store = useStore();
        const router = useRouter();
        const startDate = ref();
        const endDate = ref();
        const regions = ref(store.getters.getMetadata.regions);
        const region = ref("-1");
        const jamatkhanas = ref(store.getters.getMetadata.jamatkhanas);
        const jamatkhana = ref("-1");
        const report = ref([]);

        const error = ref(null);
        const loadingReport = ref(false);

        // default start and end dates to today
        startDate.value = new Date().toLocaleDateString('en-CA');
        endDate.value = new Date().toLocaleDateString('en-CA');

        const filterJamatkhanas = (id) => {
            let returnJks = []
            for (let jk of jamatkhanas.value) {
                if (jk.regionId === id) {
                    returnJks.push(jk)
                }
            }
            return returnJks
        }

        const disableRunReport = computed(() => {
            return !(startDate.value && endDate.value && region.value != "-1")
        })

        const getJamatkhanaName = (id) => {
            const methods = store.getters.getMetadata.jamatkhanas;
            const filteredMethod = methods.filter(method => method.id === id);
            if (filteredMethod.length === 1) {
                return filteredMethod[0].name
            } else {
                return id;
            }
        }

        const totalAmount = computed(() => {
            let total = 0;
            report.value.forEach((payment) => {
                total += payment.amount
            })
            return total
        })

        const clickViewDetails = async (paymentID) => {
            let routeData = router.resolve({name: 'PaymentStatus', params: {paymentId: paymentID}});
            window.open(routeData.href, '_blank');
        }

        const runReport = async () => {
            error.value = null;
            if (startDate.value > endDate.value) {
                error.value = "Start date cannot be later than end date";
                return;
            }
            loadingReport.value = true;

            let inputEndDate = new Date((endDate.value + 'T00:00'));
            let newEndDate = new Date(inputEndDate.setDate(inputEndDate.getDate()+1)).toLocaleDateString('en-CA');
            let params = {
                startDate: startDate.value,
                endDate: newEndDate
            }
            if (jamatkhana.value != "-1") {
                params.jamatkhanaId = jamatkhana.value;
            } else {
                params.regionId = region.value;
            }

            try {
                const response = await axios.get('/api/v1/admin/reports/returnedPayments', { params });
                const { success, statusMessage } = response.data;
                if (success) {
                    const { data } = response.data;
                    report.value = data;
                    if (report.value.length === 0) {
                        error.value = "No returned payments found from the search criteria."
                    }
                } else {
                    console.log(statusMessage);
                    error.value=statusMessage;
                }
            } catch (err) {
                console.log(err);
                error.value = err
            }

            loadingReport.value = false;
        }

        return {
            displayInUSD,
            getLocalTimestampfromUtcTimeStamp,
            getJamatkhanaName,
            loadingReport,
            disableRunReport,
            clickViewDetails,
            runReport,
            error,
            startDate,
            endDate,
            regions,
            region,
            jamatkhanas,
            jamatkhana,
            filterJamatkhanas,
            report,
            totalAmount
        }

    },
    async created() {
        let volunteer = this.$store.getters.getVolunteerInfo;
        if (!volunteer.resources.some(permission => permission.id === 13)) {
            this.$router.push("/search");
        }
        this.regions = volunteer.regions;
        if(!this.regions)
        {
            let volJkInfo = getVolunteerJks();
            this.jamatkhanas = volJkInfo.jamatkhanas;
            this.jamatkhana = volJkInfo.selectedJamatkhana;
            this.region = this.jamatkhanas[0].regionId;        
        }
    }
}
</script>

<style scoped>
.searchInputs {
    padding-bottom: 20px;
}
.searchInputRow {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
}
.searchInputRow:nth-last-child(2) {
    padding-bottom: 10px;
}
.searchInputItem {
    padding-right: 20px;
    min-width: 200px;
}
.link {
    color: #0d6efd;
    text-decoration: underline;
}
.link:hover {
    cursor: pointer;
}
.tableResults {
    padding-top: 20px;
}
button {
    margin-right: 10px;
}
</style>

